import { Injectable } from '@angular/core'
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { AnalyticsService } from '../services/analytics.service';
import { Subject } from 'rxjs';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from '../services/auth.service';
import { redirectWithParams } from './redirect.helper';

@Injectable()
export class ApiHelper {
    private tk: any;
    public apiErrorMessage: any;
    private user: any;
    public jwtHelperService: JwtHelperService = new JwtHelperService();
    private bo: any;
    userLogged: boolean;
    private userLogging = new Subject();
    userObserver = this.userLogging.asObservable();

    constructor(private httpClient: HttpClient,
        public as: AnalyticsService,
        private router: Router,
        private authService: AuthService
    ) {}

    auth(hash): Observable<boolean> {
        return this.httpClient
            .post(environment.server.api.auth + '/api/token/legacy/decode/' + hash, null)
            .pipe(map((response: any) => {
                this.logUser(response);
                return true;
            }, error => {
                return false;
            }));
    }

    loginImpersonation(username: string) {
        return this.httpClient.post(environment.server.api.auth + '/api/login/impersonation?username=' + username, null).pipe(map(
            (resp: any) => {
                if (resp && resp.data && resp.data.maintenance && resp.data.maintenance.status) {
                    this.evaluateMaintenanceRedirection(resp.data.maintenance);
                } else {
                    if (resp && resp.data && resp.data.jwt) {
                        // Store jwt token in local storage to keep user logged in between page refreshes
                        localStorage.setItem('tk', resp.data.jwt);
                    }
                    return resp;
                }
            }
        ));
    }

    login(username: string, password: string, ignoreMaintenance: boolean) {
        return this.httpClient.post(environment.server.api.auth + '/api/login', {
            username: username,
            passwordHash: password
        }).pipe(map(
            (response : any) => {
                const resp = response;
                if (resp && resp.data && !ignoreMaintenance && resp.data.maintenance && resp.data.maintenance.status) {
                    this.evaluateMaintenanceRedirection(resp.data.maintenance);
                } else {
                    if (resp && resp.data && resp.data.jwt) {
                        // Store jwt token in local storage to keep user logged in between page refreshes
                        localStorage.setItem('tk', resp.data.jwt);
                    }
                    return resp;
                }
            }
        ));
    }

    redirectImpersonated() {
        this.router.navigate(['/login-impersonificado']);
    }

    redirectLogin(response, option, returnUrl?) {
        if (response.userInfo.impersonation) {
            this.logUser(response, option);
            this.redirectImpersonated();
        } else {
            switch (response.loginRedirect) {
                case 'PORTAL_LEGACY':
                    option.subscribe(
                        urlRedirect => {
                            location.assign(urlRedirect);
                        }
                    );
                    break;
                case 'PORTAL_TD':
                    const tk = response.jwt;
                    // Login successful if there's a jwt token in the response
                    let hashedPassword: any;
                    if (tk) {
                        // Store user details
                        const user = this.jwtHelperService.decodeToken(tk);
                        hashedPassword = user.HashedPassword;
                        this.as.gtmEvent('userId', user.UserId);
                        localStorage.setItem('userInfo', JSON.stringify(response.userInfo));

                        this.authService.auth(null, tk).subscribe();
                    }
                    this.logUser(response, option || hashedPassword);
                    if (returnUrl) {
                        redirectWithParams(this.router, returnUrl);
                    } else {
                        this.router.navigate(['/inicio']);
                    }
                    break;
                default:
                    this.router.navigate(['/forbidden']);
                    break;
            }
        }
    }

    isAppMaintenance() {
        return this.httpClient.get(environment.server.api.auth + '/api/maintenance').subscribe(
            (response : any) => {
                const resp = response;
                if (resp && resp.data && resp.status && resp.status.code === 0) {
                    this.evaluateMaintenanceRedirection(resp.data);
                }
            },
            (error) => {
                this.router.navigate(['/forbidden']);
            }
        );
    }

    private evaluateMaintenanceRedirection(response) {
        if (response.status) {
            this.router.navigate(['/maintenance']);
        }
    }

    logUser(response, pass = null) {
        let data = response;
        data = data.data || data;
        const tk = data && data.jwt;
        const passHashed = pass || data && data.legacy && data.legacy.hashedPassword;
        if (passHashed) {
            localStorage.setItem('hash', passHashed);
        }
        // Login successful if there's a jwt token in the response
        if (tk) {
            this.tk = tk;
            this.user = this.tk && this.jwtHelperService.decodeToken(this.tk);
            this.as.setGTMUser(this.user.UserId);
            localStorage.setItem('tk', tk);
            localStorage.setItem('reset', '1');
            localStorage.setItem('currentUser', this.user && JSON.stringify(this.user));
            // Remove protected hash
            localStorage.removeItem('protected_hash');
            this.userLogged = true;
            this.userLogging.next({
                tk: tk,
                user: this.user && JSON.stringify(this.user)
            })
        } else {
            this.userLogged = false;
            this.userLogging.next({
                tk: null,
                user: null
            })
        }
    }

    getWithoutResponse(url: string, data = null, header = null, responseType = null) {
        const options = {};
        if (header) {
            options['headers'] = this.setHeaders(header);
        }
        if (data) {
            options['search'] = data;
        }
        if (responseType) {
            options['responseType'] = 'blob';
        }

        return this.httpClient.get(url, options)
    }

    setHeaders(data) {
        const header = new HttpHeaders(data);
        return header;
    }
}
